import React, { useState, useEffect } from 'react';
import axios from '@/utils/api';
import { 
  Crown, 
  Star, 
  Gift, 
  Building2, 
  Save, 
  Plus, 
  Trash2, 
  ChevronDown, 
  ChevronUp,
  Check,
  X,
  Sparkles,
  Edit2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

// Use same-origin API path on production custom domains
const API = window.location.hostname.includes('preview.emergentagent.com') || window.location.hostname === 'localhost'
  ? (process.env.REACT_APP_BACKEND_URL || '')
  : '';

// Default tier configuration
const DEFAULT_TIER_CONFIG = {
  founding_member: {
    label: 'Founding Member',
    icon: 'crown',
    color: '#fbbf24',
    bgGradient: 'from-yellow-500 to-amber-600',
    badgeTitle: 'Founding Member',
    badgeDescription: 'Early supporter with lifetime access',
    statusBadge: 'Lifetime Access',
    statusMessage: "You'll never pay for premium",
    footerMessage: "You joined before we launched premium. As a thank you, you have full lifetime access to every feature — forever.",
    features: [
      { name: 'AI Coach Reflections', included: true },
      { name: 'Voice Typing', included: true },
      { name: 'Unlimited Entries', included: true },
      { name: 'PDF Export', included: true },
      { name: 'Mood Tracking', included: true },
      { name: 'Badges & Streaks', included: true },
      { name: 'Priority Support', included: true },
    ],
    priceDisplay: 'FREE FOREVER',
    sortOrder: 1
  },
  premium: {
    label: 'Premium',
    icon: 'star',
    color: '#df1283',
    bgGradient: 'from-pink-500 to-rose-600',
    badgeTitle: 'Premium Member',
    badgeDescription: 'Full access to all features',
    statusBadge: 'Premium',
    statusMessage: 'Enjoy all premium features',
    footerMessage: 'Thank you for being a premium member!',
    features: [
      { name: 'AI Coach Reflections', included: true },
      { name: 'Voice Typing', included: true },
      { name: 'Unlimited Entries', included: true },
      { name: 'PDF Export', included: true },
      { name: 'Mood Tracking', included: true },
      { name: 'Badges & Streaks', included: true },
      { name: 'Priority Support', included: false },
    ],
    priceDisplay: '$9.99/month',
    sortOrder: 2
  },
  free: {
    label: 'Free',
    icon: 'gift',
    color: '#6366f1',
    bgGradient: 'from-indigo-500 to-purple-600',
    badgeTitle: 'Free Plan',
    badgeDescription: 'Basic journaling features',
    statusBadge: 'Free',
    statusMessage: 'Upgrade to unlock more features',
    footerMessage: 'Upgrade to premium to unlock all features!',
    features: [
      { name: 'Basic Journal Entries', included: true },
      { name: 'AI Coach Reflections', included: false },
      { name: 'Voice Typing', included: false },
      { name: 'PDF Export', included: false },
      { name: 'Mood Tracking', included: true },
      { name: 'Badges & Streaks', included: true },
      { name: 'Priority Support', included: false },
    ],
    priceDisplay: 'FREE',
    sortOrder: 3
  },
  enterprise: {
    label: 'Enterprise',
    icon: 'building',
    color: '#0FFFE2',
    bgGradient: 'from-cyan-500 to-teal-600',
    badgeTitle: 'Enterprise',
    badgeDescription: 'Custom solution for organizations',
    statusBadge: 'Enterprise',
    statusMessage: 'White-label B2B solution',
    footerMessage: 'Powered by your organization',
    features: [
      { name: 'All Premium Features', included: true },
      { name: 'Custom Branding', included: true },
      { name: 'Admin Dashboard', included: true },
      { name: 'User Management', included: true },
      { name: 'Analytics', included: true },
      { name: 'Custom Domain', included: true },
      { name: 'Priority Support', included: true },
    ],
    priceDisplay: 'Contact Sales',
    sortOrder: 4
  }
};

const ICONS = {
  crown: Crown,
  star: Star,
  gift: Gift,
  building: Building2,
  sparkles: Sparkles
};

const TierEditor = () => {
  const [tierConfig, setTierConfig] = useState(DEFAULT_TIER_CONFIG);
  const [expandedTier, setExpandedTier] = useState(null);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchTierConfig();
  }, []);

  // SECURITY: Auth handled via httpOnly cookies
  const fetchTierConfig = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/tier-config`, {
        withCredentials: true
      });
      if (response.data.config) {
        setTierConfig({ ...DEFAULT_TIER_CONFIG, ...response.data.config });
      }
    } catch (error) {
      console.log('Using default tier config');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings/tier-config`, 
        { config: tierConfig },
        { withCredentials: true }
      );
      toast.success('Tier configuration saved!');
      setHasChanges(false);
    } catch (error) {
      toast.error('Failed to save tier configuration');
    } finally {
      setSaving(false);
    }
  };

  const updateTier = (tierKey, field, value) => {
    setTierConfig(prev => ({
      ...prev,
      [tierKey]: {
        ...prev[tierKey],
        [field]: value
      }
    }));
    setHasChanges(true);
  };

  const updateFeature = (tierKey, featureIndex, field, value) => {
    setTierConfig(prev => ({
      ...prev,
      [tierKey]: {
        ...prev[tierKey],
        features: (prev[tierKey].features || []).map((f, i) => 
          i === featureIndex ? { ...f, [field]: value } : f
        )
      }
    }));
    setHasChanges(true);
  };

  const addFeature = (tierKey) => {
    setTierConfig(prev => ({
      ...prev,
      [tierKey]: {
        ...prev[tierKey],
        features: [...(prev[tierKey].features || []), { id: `feature-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, name: 'New Feature', included: true }]
      }
    }));
    setHasChanges(true);
  };

  const removeFeature = (tierKey, featureIndex) => {
    setTierConfig(prev => ({
      ...prev,
      [tierKey]: {
        ...prev[tierKey],
        features: (prev[tierKey].features || []).filter((_, i) => i !== featureIndex)
      }
    }));
    setHasChanges(true);
  };

  const renderTierCard = (tierKey) => {
    const tier = tierConfig[tierKey];
    if (!tier) return null; // Guard against undefined tier
    const isExpanded = expandedTier === tierKey;
    const IconComponent = ICONS[tier.icon] || Star;

    return (
      <div 
        key={tierKey}
        className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl overflow-hidden"
      >
        {/* Tier Header */}
        <div 
          className="p-4 flex items-center justify-between cursor-pointer hover:bg-[#4A0A66]/20"
          onClick={() => setExpandedTier(isExpanded ? null : tierKey)}
        >
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: `${tier.color}20`, border: `2px solid ${tier.color}` }}
            >
              <IconComponent className="w-5 h-5" style={{ color: tier.color }} />
            </div>
            <div>
              <h3 className="text-white font-semibold">{tier.label}</h3>
              <p className="text-xs text-[#FDF2F8]/50">{tier.badgeDescription}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm font-mono px-3 py-1 rounded-full" style={{ backgroundColor: `${tier.color}20`, color: tier.color }}>
              {tier.priceDisplay}
            </span>
            {isExpanded ? (
              <ChevronUp className="w-5 h-5 text-[#FDF2F8]/50" />
            ) : (
              <ChevronDown className="w-5 h-5 text-[#FDF2F8]/50" />
            )}
          </div>
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="border-t border-[#4A0A66] p-4 space-y-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-1">Display Label</label>
                <Input
                  value={tier.label}
                  onChange={(e) => updateTier(tierKey, 'label', e.target.value)}
                  className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-1">Price Display</label>
                <Input
                  value={tier.priceDisplay}
                  onChange={(e) => updateTier(tierKey, 'priceDisplay', e.target.value)}
                  className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                />
              </div>
            </div>

            {/* Badge Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-1">Badge Title</label>
                <Input
                  value={tier.badgeTitle}
                  onChange={(e) => updateTier(tierKey, 'badgeTitle', e.target.value)}
                  className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-1">Badge Description</label>
                <Input
                  value={tier.badgeDescription}
                  onChange={(e) => updateTier(tierKey, 'badgeDescription', e.target.value)}
                  className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                />
              </div>
            </div>

            {/* Status Badge */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-1">Status Badge Text</label>
                <Input
                  value={tier.statusBadge}
                  onChange={(e) => updateTier(tierKey, 'statusBadge', e.target.value)}
                  className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                  placeholder="e.g., Lifetime Access"
                />
              </div>
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-1">Status Message</label>
                <Input
                  value={tier.statusMessage}
                  onChange={(e) => updateTier(tierKey, 'statusMessage', e.target.value)}
                  className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                  placeholder="e.g., You'll never pay for premium"
                />
              </div>
            </div>

            {/* Footer Message */}
            <div>
              <label className="block text-sm text-[#FDF2F8]/70 mb-1">Footer Message (shown in badge)</label>
              <Input
                value={tier.footerMessage}
                onChange={(e) => updateTier(tierKey, 'footerMessage', e.target.value)}
                className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
              />
            </div>

            {/* Color */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-1">Brand Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={tier.color}
                    onChange={(e) => updateTier(tierKey, 'color', e.target.value)}
                    className="w-10 h-10 rounded-full cursor-pointer bg-transparent border-2 border-[#4A0A66] appearance-none [&::-webkit-color-swatch-wrapper]:p-0.5 [&::-webkit-color-swatch]:rounded-full [&::-webkit-color-swatch]:border-0"
                  />
                  <Input
                    value={tier.color}
                    onChange={(e) => updateTier(tierKey, 'color', e.target.value)}
                    className="bg-[#4A0A66]/30 border-[#4A0A66] text-white flex-1"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-1">Icon</label>
                <select
                  value={tier.icon}
                  onChange={(e) => updateTier(tierKey, 'icon', e.target.value)}
                  className="w-full bg-[#4A0A66]/30 border border-[#4A0A66] text-white rounded-lg px-3 py-2"
                >
                  <option value="crown">Crown</option>
                  <option value="star">Star</option>
                  <option value="gift">Gift</option>
                  <option value="building">Building</option>
                  <option value="sparkles">Sparkles</option>
                </select>
              </div>
            </div>

            {/* Features List */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm text-[#FDF2F8]/70">Included Features</label>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="text-xs border-[#4A0A66] text-[#0FFFE2]"
                  onClick={() => addFeature(tierKey)}
                >
                  <Plus className="w-3 h-3 mr-1" /> Add Feature
                </Button>
              </div>
              <div className="space-y-2">
                {(tier.features || []).map((feature, index) => (
                  <div key={feature.id || `feature-${index}`} className="flex items-center gap-2 bg-[#4A0A66]/20 rounded-lg p-2">
                    <button
                      type="button"
                      onClick={() => updateFeature(tierKey, index, 'included', !feature.included)}
                      className={`w-6 h-6 rounded flex items-center justify-center ${
                        feature.included ? 'bg-[#22c55e] text-white' : 'bg-[#4A0A66] text-[#FDF2F8]/30'
                      }`}
                    >
                      {feature.included ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                    </button>
                    <Input
                      value={feature.name}
                      onChange={(e) => updateFeature(tierKey, index, 'name', e.target.value)}
                      className="bg-transparent border-0 text-white flex-1 h-8"
                    />
                    <button
                      type="button"
                      onClick={() => removeFeature(tierKey, index)}
                      className="text-red-400 hover:text-red-300 p-1"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Membership Tier Configuration</h3>
          <p className="text-sm text-[#FDF2F8]/60">
            Customize tier names, descriptions, features, and badge content
          </p>
        </div>
        {hasChanges && (
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-gradient-to-r from-[#df1283] to-[#FF7901]"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        )}
      </div>

      <div className="space-y-4">
        {Object.keys(tierConfig)
          .filter(tierKey => tierConfig[tierKey]) // Filter out undefined tiers
          .sort((a, b) => (tierConfig[a]?.sortOrder || 0) - (tierConfig[b]?.sortOrder || 0))
          .map(tierKey => renderTierCard(tierKey))}
      </div>
    </div>
  );
};

export default TierEditor;
